import { api } from '../../server/api';
import { getSearchParams } from '../../utils/getSearchParams';
import { Captcha, RegisterError, SignupApi, SignupPayload } from './signupFlow';

export const REGISTRATION_ORIGIN_AI_CHAT = 'ai_chat';

interface ActionMgm {
  playerId?: string;
  actionId?: string;
  clientId?: string;
}

/**
 * Indicação (MGM) da visita atual: vem da URL ou do sessionStorage gravado
 * pela tela de login. Mesma regra do formulário de cadastro.
 */
function getActionMgm(): ActionMgm {
  const action = getSearchParams('action');
  const clientId = getSearchParams('client');
  const playerId = getSearchParams('player');
  if (action && clientId && playerId) {
    return {
      playerId: String(playerId),
      actionId: String(action),
      clientId: String(clientId),
    };
  }

  const stored = (key: string) =>
    sessionStorage.getItem(key)?.replace(/["]/g, '');
  if (stored('action') && stored('clientId') && stored('playerId')) {
    return {
      playerId: stored('playerId'),
      actionId: stored('action'),
      clientId: stored('clientId'),
    };
  }

  return {};
}

/**
 * Captcha de conta simples, gerado e conferido no navegador, igual ao
 * CaptchaBasic usado no login.
 */
function createLocalCaptcha() {
  let current: { id: string; answer: number } | null = null;

  return {
    async getCaptcha(): Promise<Captcha> {
      const a = Math.floor(Math.random() * 10) + 1;
      const b = Math.floor(Math.random() * 10) + 1;
      current = { id: String(Date.now()), answer: a + b };
      return { id: current.id, pergunta: `${a} + ${b}` };
    },
    async verifyCaptcha({ id, resposta }: { id: string; resposta: string }) {
      return (
        !!current &&
        current.id === id &&
        Number(resposta.trim()) === current.answer
      );
    },
  };
}

interface AiSignupApiOptions {
  /** Indicador vindo da página (ex.: landing do prospector). A indicação da URL tem prioridade. */
  hostId?: string;
}

/** Adaptador do chat para a API do OQ (cadastro via /createPlayer). */
export function createAiSignupApi(
  client: any,
  { hostId }: AiSignupApiOptions = {}
): SignupApi {
  const captcha = createLocalCaptcha();

  return {
    ...captcha,
    async register(payload: SignupPayload) {
      const actionMgm = getActionMgm();
      const parameters = {
        actionMgm,
        actionId: actionMgm.actionId,
        player: {
          ...payload,
          confirmPassword: payload.password,
          clientId: client.id,
          hostId: actionMgm.playerId || hostId || null,
          viaIframe: false,
          registrationOrigin: REGISTRATION_ORIGIN_AI_CHAT,
        },
        targetPublic: client.targetPublic,
        client,
        page: 'register',
      };

      const { data } = await api.post('/createPlayer', parameters);

      const status = data?.data?.status;
      const code = data?.data?.code;
      if (status === 'error/user-exists-other-client') {
        throw new RegisterError('E-mail já cadastrado', 'EMAIL_EXISTS');
      }
      if (code === 'auth/invalid-password') {
        throw new RegisterError('Senha inválida', 'INVALID_PASSWORD');
      }
      if (status || data?.success === false) {
        throw new RegisterError(data?.message || 'Falha no cadastro');
      }

      return data.data;
    },
  };
}

/** Termos de uso do cliente (HTML cadastrado no admin). */
export async function getClientTerms(
  clientId: string
): Promise<{ htmlString?: string; termsImage?: string } | null> {
  const { data } = await api.post('/search', {
    searchFunctionality: 'getClientTerms',
    userType: 'player',
    clientId,
  });
  return data?.success ? data.data : null;
}
